import { dispatch, ACTION_TYPES } from '../actions/index';

// Drives the undo feature (see store/index.js's submitAction and
// undoLastAction): undo must never let a player back out of an action once
// they've seen information they didn't have before, since that's what turns
// "undo a misclick" into "reroll a draw until I like it."
//
// Every card instance in the game is created once at setup (see
// createGameState.js) and only ever moves between containers - it's never
// created lazily on draw. So "revealed new information" isn't about an
// instance coming into existence, it's about an instance moving out of a
// *hidden* container (a shuffled deck, whose order nobody knows) into any
// *visible* one (a hand, the dungeon hall, a discard pile, etc). Reshuffling
// a face-up discard pile back into a deck is the reverse direction (visible
// to hidden) and correctly does NOT count on its own - only actually drawing
// from the freshly-shuffled deck afterward does, exactly as it should.
function collectHiddenInstanceIds(state) {
  const hidden = new Set();
  for (const id of state.dungeonDeck) hidden.add(id);
  for (const id of state.village.curseDeck) hidden.add(id);
  for (const id of state.village.familiarDeck) hidden.add(id);
  for (const player of state.players) {
    for (const id of player.deck) hidden.add(id);
  }
  return hidden;
}

export function revealsNewInformation(prevState, nextState) {
  const prevHidden = collectHiddenInstanceIds(prevState);
  const nextHidden = collectHiddenInstanceIds(nextState);
  for (const id of prevHidden) {
    if (!nextHidden.has(id)) return true;
  }
  return false;
}

// Brian (bug report, 2026-08-15): "I accidentally clicked to enter the
// dungeon and I'm not able to undo. I should be able to undo." His entry
// resolved two Horror Incarnate "Trophy Dungeon: Gain 1 curse" trophies -
// two curses left the hidden curse deck, which correctly locked undo (see
// revealsNewInformation above; letting him un-see those draws is exactly
// the "reroll a draw" exploit the lock exists for). The fix is upstream of
// undo: the UI asks for confirmation BEFORE an entry that would lock it
// (PlayerDock's confirmingDungeonEntry, same two-click pattern as
// confirmingEndTurn), and stays one-click for ordinary, still-undoable
// entries. Dry-running the real reducer is safe (pure function, result
// discarded) and leak-free: only WHETHER something would be revealed is
// surfaced, which is already derivable from the face-up cards that trigger
// entry effects (hall Globals, hand trophies), never WHAT gets drawn.
export function dungeonEntryLocksUndo(state, playerId) {
  try {
    const next = dispatch(state, { type: ACTION_TYPES.ENTER_DUNGEON, payload: { playerId } });
    return revealsNewInformation(state, next) || Boolean(next.meta.pendingCombatContinuation);
  } catch {
    // Not currently a legal entry (wrong phase/player) - nothing to warn
    // about; the real dispatch will surface the error to the player.
    return false;
  }
}
